import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { DealDto, DealNoteDto, DealPhase, DealStage, ServiceDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { useNavState } from '../navState';
import { Icon, type IconName } from '../icons';
import { euro, euroInput, parseEuro } from '../format';
import { ErrorBanner, Field, Modal, More, Panel } from '../ui';
import { DateInput } from '../pickers';
import { ask, askText } from '../dialogs';
import { DealFiles } from './DealFiles';

const PHASES: { key: DealPhase; n: number; label: string; hint: string }[] = [
  { key: 'lead', n: 1, label: 'Segnalazione', hint: 'Nuovi contatti: fissa un appuntamento' },
  { key: 'meeting', n: 2, label: 'Appuntamento', hint: 'Visite fissate' },
  { key: 'visited', n: 3, label: 'Visita fatta', hint: 'Prepara il preventivo' },
  { key: 'quote', n: 4, label: 'Preventivo', hint: 'In attesa di risposta' },
  { key: 'client', n: 5, label: 'Cliente', hint: 'Lavoro e incasso' },
];

/** Come l'abbiamo conosciuto: lista fissa; «Commerciale …» assegna la trattativa a quel commerciale. */
const sourcesFor = (agents: string[]) => ['Passaparola', 'Segnalato', ...(agents.length ? agents.map((a) => `Commerciale ${a}`) : ['Commerciale']), 'Evento', 'Altro'];

const QUOTE_LABEL: Record<string, string> = { draft: 'bozza', ready: 'pronto da inviare', sent: 'inviato', accepted: 'accettato', rejected: 'rifiutato', expired: 'scaduto' };
const whenIt = (iso: string) => new Date(iso).toLocaleString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Percorso clienti: ogni segnalazione avanza da sola di fase
 * (appuntamento fissato, visita fatta, preventivo, cliente) e ogni scheda ha un solo tasto: il prossimo passo.
 */
export function DealsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const isAdmin = ctx.me.user.role === 'admin';
  const [deals, setDeals] = useState<DealDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useNavState<number | 'new' | null>(ctx, 'deal', null);
  const [showLost, setShowLost] = useState(false);
  const [q, setQ] = useState('');
  const param = (ctx.pageParam ?? null) as { newFromContact?: Prefill; newLead?: boolean } | null;
  const fromContact = param?.newFromContact ?? null;
  useEffect(() => {
    if (fromContact || param?.newLead) setOpenId('new');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pageParam]);

  const load = useCallback(async () => {
    try {
      setDeals(await api.get<DealDto[]>('/api/deals'));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (deals ?? []).filter((d) => !s || `${d.name} ${d.contactName ?? ''} ${d.email ?? ''} ${d.services.map((x) => x.name).join(' ')}`.toLowerCase().includes(s));
  }, [deals, q]);
  const lost = visible.filter((d) => d.phase === 'lost');
  const open = openId && openId !== 'new' ? (deals?.find((d) => d.id === openId) ?? null) : null;

  const quoteFor = (d: DealDto) => ctx.setPage('quotes', { newFromDeal: { dealId: d.id, name: d.name, email: d.email ?? '', value: d.valueCents } });

  /** Il prossimo passo di ogni fase: un solo tasto. */
  const next = (d: DealDto): { label: string; icon: IconName; hint: string; go: () => void } | null => {
    switch (d.phase) {
      case 'lead':
        return { label: 'Fissa appuntamento', icon: 'calendar', hint: 'Fissa una visita o una call: la scheda passa ad «Appuntamento»', go: () => ctx.setPage('visits', { newVisit: { dealId: d.id, contactId: d.contactId } }) };
      case 'meeting':
        return { label: 'Apri visita', icon: 'mic', hint: 'Apri la visita fissata: lì la registri e hai il riassunto', go: () => ctx.setPage('visits', { open: d.visitId }) };
      case 'visited':
        return { label: 'Prepara preventivo', icon: 'note', hint: 'Prepara il preventivo per questa azienda', go: () => quoteFor(d) };
      case 'quote':
        return d.quoteId
          ? { label: 'Apri preventivo', icon: 'note', hint: 'Apri il preventivo mandato', go: () => ctx.setPage('quotes', { open: d.quoteId }) }
          : { label: 'Prepara preventivo', icon: 'note', hint: 'Prepara il preventivo per questa azienda', go: () => quoteFor(d) };
      case 'client':
        if (!d.clientId && isAdmin) return { label: 'Crea scheda cliente', icon: 'building', hint: 'Crea il cliente con tariffa e accesso, già collegato a questa trattativa', go: () => ctx.setPage('clients', { newClient: { name: d.name, phones: d.phone, referrerUserId: d.ownerId !== ctx.me.user.id ? d.ownerId : null, managerUserId: d.ownerId !== ctx.me.user.id ? d.ownerId : null, dealId: d.id } }) };
        return { label: 'Avvia lavoro', icon: 'play', hint: 'Apri «Ore di lavoro» per contare il tempo per questo cliente', go: () => ctx.setPage('sessions') };
      default:
        return null;
    }
  };

  const card = (d: DealDto) => {
    const n = next(d);
    const info = [d.valueCents ? euro(d.valueCents) : '', d.source ?? '', isAdmin ? d.ownerName : ''].filter(Boolean).join(' · ');
    return (
      <div key={d.id} className="kan-card path-card" onClick={() => setOpenId(d.id)}>
        <div className="kan-name">{d.name}</div>
        {d.contactName && <div className="muted small">{d.contactName}</div>}
        {d.services.length > 0 && (
          <div className="svc-tags">
            {d.services.map((s) => (
              <span key={s.id} className="svc-tag">
                {s.name}
              </span>
            ))}
          </div>
        )}
        {d.visitAt && (
          <div className="kan-due">
            <Icon name="calendar" size={12} /> {whenIt(d.visitAt)}
          </div>
        )}
        {d.phase === 'quote' && d.quoteStatus && <div className="muted small">Preventivo {QUOTE_LABEL[d.quoteStatus] ?? d.quoteStatus}</div>}
        {info && <div className="muted small">{info}</div>}
        {n && (
          <div className="path-actions">
            <button
              type="button"
              className="btn small dark"
              title={n.hint}
              onClick={(e) => {
                e.stopPropagation();
                n.go();
              }}
            >
              <Icon name={n.icon} size={14} /> {n.label}
            </button>
            {d.phase === 'lead' && (
              <button
                type="button"
                className="btn small ghost"
                title="Il preventivo si può preparare anche prima della visita"
                onClick={(e) => {
                  e.stopPropagation();
                  quoteFor(d);
                }}
              >
                Preventivo
              </button>
            )}
            {d.phase === 'client' && (
              <button
                type="button"
                className="btn small ghost"
                title="Fai una fattura a questo cliente"
                onClick={(e) => {
                  e.stopPropagation();
                  ctx.setPage('invoices');
                }}
              >
                Fattura
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="panels single">
      <Panel
        title="Percorso clienti"
        subtitle="Dalla segnalazione al cliente: il tasto su ogni scheda è il prossimo passo"
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            <input className="deal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca" />
            <button type="button" className="btn dark" title="Un possibile cliente da contattare" onClick={() => setOpenId('new')}>
              <Icon name="plus" size={16} /> Nuova segnalazione
            </button>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {!deals ? (
          <div className="muted small">Caricamento…</div>
        ) : (
          <>
            <div className="kanban">
              {PHASES.map((ph) => {
                const list = visible.filter((d) => d.phase === ph.key);
                return (
                  <div key={ph.key} className="kan-col">
                    <div className="kan-head">
                      <b>
                        <span className="path-n">{ph.n}</span> {ph.label} <span className="muted small">{list.length || ''}</span>
                      </b>
                      <span className="muted small">{ph.hint}</span>
                    </div>
                    <div className="kan-cards">
                      {list.map(card)}
                      {list.length === 0 && <div className="muted small kan-empty">Nessuno</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            {lost.length > 0 && (
              <>
                <button type="button" className="btn ghost small" style={{ marginTop: 12, alignSelf: 'flex-start' }} title="Le trattative che non sono andate in porto" onClick={() => setShowLost((v) => !v)}>
                  {showLost ? 'Nascondi' : 'Mostra'} perse ({lost.length})
                </button>
                {showLost && (
                  <div className="ag-list" style={{ marginTop: 8 }}>
                    {lost.map((d) => (
                      <div key={d.id} className="ag-row done" onClick={() => setOpenId(d.id)}>
                        <span className="ag-title">{d.name}</span>
                        <span className="muted small">{d.lostReason ?? ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Panel>

      {openId && (
        <DealModal
          ctx={ctx}
          prefill={openId === 'new' ? fromContact : null}
          deal={open}
          onClose={() => setOpenId(null)}
          onSaved={() => {
            setOpenId(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

interface Prefill {
  name: string;
  contactName: string;
  email: string;
  phone: string;
}

// ---------------------------------------------------------------------------

/** Segnalazione nuova o scheda della trattativa: chi è, come l'abbiamo conosciuto, cosa vuole. */
export function DealModal({ ctx, deal, prefill, onClose, onSaved }: { ctx: Ctx; deal: DealDto | null; prefill?: Prefill | null; onClose: () => void; onSaved: () => void }) {
  const { api } = ctx;
  const [name, setName] = useState(deal?.name ?? prefill?.name ?? '');
  const [contact, setContact] = useState(deal?.contactName ?? prefill?.contactName ?? '');
  const [email, setEmail] = useState(deal?.email ?? prefill?.email ?? '');
  const [phone, setPhone] = useState(deal?.phone ?? prefill?.phone ?? '');
  const [source, setSource] = useState(deal?.source ?? '');
  const [agents, setAgents] = useState<string[]>([]);
  useEffect(() => {
    api
      .get<{ id: number; name: string }[]>('/api/deals/agents')
      .then((l) => setAgents(l.map((a) => a.name)))
      .catch(() => undefined);
  }, [api]);
  const sources = sourcesFor(agents);
  const [serviceIds, setServiceIds] = useState<number[]>(deal?.services.map((s) => s.id) ?? []);
  const [stage, setStage] = useState<DealStage>(deal?.stage ?? 'new');
  const [value, setValue] = useState(deal?.valueCents ? euroInput(deal.valueCents) : '');
  const [notes, setNotes] = useState(deal?.notes ?? '');
  const [nextDate, setNextDate] = useState(deal?.nextActionDate ?? '');
  const [nextText, setNextText] = useState(deal?.nextActionText ?? '');
  const [lost, setLost] = useState(deal?.lostReason ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<DealNoteDto[]>([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!deal) return;
    api.get<{ notes: DealNoteDto[] }>(`/api/deals/${deal.id}`).then((r) => setTimeline(r.notes)).catch(() => undefined);
  }, [api, deal]);

  const body = (st: DealStage = stage) => ({
    name: name.trim(),
    contactName: contact.trim() || null,
    email: email.trim() || null,
    phone: phone.trim() || null,
    source: source || null,
    stage: st,
    valueCents: parseEuro(value) ?? 0,
    notes: notes.trim() || null,
    nextActionDate: nextDate || null,
    nextActionText: nextText.trim() || null,
    lostReason: st === 'lost' ? lost.trim() || null : null,
    clientId: deal?.clientId ?? null,
    contactId: deal?.contactId ?? null,
    serviceIds,
  });

  /** Salva e restituisce l'id (nuova o esistente). */
  const persist = async (st: DealStage = stage): Promise<number | null> => {
    if (!name.trim()) {
      setError('Indica il nome della società o della persona');
      return null;
    }
    setBusy(true);
    setError(null);
    try {
      if (deal) {
        await api.put(`/api/deals/${deal.id}`, body(st));
        return deal.id;
      }
      return (await api.post<DealDto>('/api/deals', body(st))).id;
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
      return null;
    }
  };

  const save = async () => {
    if ((await persist()) != null) onSaved();
  };

  const saveAndMeet = async () => {
    const id = await persist();
    if (id != null) ctx.setPage('visits', { newVisit: { dealId: id, contactId: deal?.contactId ?? null } });
  };

  const makeQuote = async () => {
    const id = await persist();
    if (id != null) ctx.setPage('quotes', { newFromDeal: { dealId: id, name: name.trim(), email: email.trim(), value: parseEuro(value) ?? 0 } });
  };

  const markLost = async () => {
    const why = await askText('Perché è persa? (facoltativo)', lost);
    if (why === null) return;
    setLost(why);
    setStage('lost');
    if (!deal) return;
    try {
      await api.put(`/api/deals/${deal.id}`, { ...body('lost'), lostReason: why.trim() || null });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const reopen = async () => {
    if (!deal) return;
    try {
      await api.put(`/api/deals/${deal.id}`, body('new'));
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async () => {
    if (!deal || !(await ask(`Eliminare "${deal.name}"?`))) return;
    try {
      await api.del(`/api/deals/${deal.id}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const addNote = async () => {
    if (!deal || !note.trim()) return;
    try {
      setTimeline(await api.post<DealNoteDto[]>(`/api/deals/${deal.id}/notes`, { body: note.trim() }));
      setNote('');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const isLost = deal?.stage === 'lost';

  return (
    <Modal
      title={deal ? deal.name : 'Nuova segnalazione'}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          {deal && (
            <button type="button" className="btn ghost" onClick={() => void remove()} disabled={busy} title="Elimina la trattativa">
              <Icon name="trash" size={15} />
            </button>
          )}
          {deal && !isLost && deal.phase !== 'client' && (
            <button type="button" className="btn ghost" onClick={() => void markLost()} disabled={busy} title="Non è andata: esce dal percorso (si può riaprire)">
              Persa
            </button>
          )}
          {isLost && (
            <button type="button" className="btn ghost" onClick={() => void reopen()} disabled={busy} title="Rimettila nel percorso">
              Riapri
            </button>
          )}
          <button type="button" className="btn" onClick={() => void makeQuote()} disabled={busy} title="Salva e prepara un preventivo">
            <Icon name="note" size={15} /> Preventivo
          </button>
          <button type="button" className="btn" onClick={() => void save()} disabled={busy}>
            {busy ? 'Salvataggio…' : 'Salva'}
          </button>
          {(!deal || deal.phase === 'lead') && (
            <button type="button" className="btn dark" onClick={() => void saveAndMeet()} disabled={busy}>
              <Icon name="calendar" size={15} /> {deal ? 'Fissa appuntamento' : 'Salva e fissa appuntamento'}
            </button>
          )}
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="fields-2">
        <Field label="Società o persona">
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus={!deal} placeholder="Es. OMI Group" />
        </Field>
        <Field label="Referente">
          <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Nome e cognome" />
        </Field>
        <Field label="Telefono">
          <input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </div>
      <Field label="Come l'abbiamo conosciuto" hint={source.startsWith('Commerciale ') ? 'La segue questo commerciale: se diventa cliente, la segnalazione è sua' : 'Facoltativo'}>
        <select value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">—</option>
          {[...new Set([...(source && !sources.includes(source) ? [source] : []), ...sources])].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Cosa vuole (o potrebbe volere)">
        <ServicePicker ctx={ctx} value={serviceIds} onChange={setServiceIds} />
      </Field>
      <Field label="Nota">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Es. ha già un fornitore, sentire dopo l'estate…" />
      </Field>
      {isLost && (
        <Field label="Motivo per cui è persa">
          <input value={lost} onChange={(e) => setLost(e.target.value)} placeholder="Prezzo, tempi, scelto altro…" />
        </Field>
      )}
      <More open={!!(value || nextDate || nextText)}>
        <div className="fields-2">
          <Field label="Valore stimato (€)">
            <input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" placeholder="Se lo sai già" />
          </Field>
          <Field label="Promemoria: quando">
            <DateInput value={nextDate} onChange={setNextDate} allowEmpty placeholder="Nessun promemoria" />
          </Field>
        </div>
        <Field label="Promemoria: cosa">
          <input value={nextText} onChange={(e) => setNextText(e.target.value)} placeholder="Richiamare, mandare materiale…" />
        </Field>
      </More>

      {deal && <DealFiles ctx={ctx} dealId={deal.id} />}

      {deal && (
        <div className="deal-log">
          <div className="muted small" style={{ marginBottom: 6 }}>
            Diario dei contatti
          </div>
          <div className="row-actions" style={{ margin: '0 0 10px' }}>
            <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void addNote()} placeholder="Es. chiamato, richiede sconto…" style={{ flex: 1 }} />
            <button type="button" className="btn" onClick={() => void addNote()} disabled={!note.trim()}>
              Aggiungi
            </button>
          </div>
          {timeline.map((n) => (
            <div key={n.id} className="deal-log-item">
              <div className="muted small">
                {n.userName} · {new Date(n.createdAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{n.body}</div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** Servizi da spuntare; se manca, «+ Nuovo servizio» lo aggiunge alla lista per tutti. */
function ServicePicker({ ctx, value, onChange }: { ctx: Ctx; value: number[]; onChange: (ids: number[]) => void }) {
  const [list, setList] = useState<ServiceDto[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ctx.api
      .get<ServiceDto[]>('/api/deals/services')
      .then(setList)
      .catch((e) => setError((e as Error).message));
  }, [ctx.api]);

  const toggle = (id: number) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  return (
    <div className="svc-chips">
      {error && <div className="muted small">{error}</div>}
      {(list ?? []).map((s) => (
        <button key={s.id} type="button" className={`chip${value.includes(s.id) ? ' on' : ''}`} onClick={() => toggle(s.id)}>
          {value.includes(s.id) && <Icon name="check" size={13} />} {s.name}
        </button>
      ))}
      <button type="button" className="chip add" title="Aggiungi un servizio che non c’è nella lista" onClick={() => setAdding(true)}>
        <Icon name="plus" size={13} /> Nuovo servizio
      </button>
      {adding && (
        <NewService
          ctx={ctx}
          onClose={() => setAdding(false)}
          onAdded={(s) => {
            setList((l) => [...(l ?? []), s].sort((a, b) => a.name.localeCompare(b.name)));
            onChange([...value, s.id]);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

/** Piccola finestra sopra la scheda: Esc chiude solo lei. */
function NewService({ ctx, onClose, onAdded }: { ctx: Ctx; onClose: () => void; onAdded: (s: ServiceDto) => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [onClose]);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      onAdded(await ctx.api.post<ServiceDto>('/api/deals/services', { name: name.trim() }));
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal narrow">
        <div className="modal-header">
          <h3>Nuovo servizio</h3>
        </div>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <Field label="Nome del servizio" hint="Resta nella lista anche per le prossime segnalazioni">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void add();
              }
            }}
            autoFocus
            placeholder="Es. Backup in cloud"
            maxLength={120}
          />
        </Field>
        <div className="modal-footer quote-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void add()} disabled={busy || !name.trim()}>
            Aggiungi
          </button>
        </div>
      </div>
    </div>
  );
}
